import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

/**
 * Componente para renderizar animações Lottie
 * Usa lottie-web para reproduzir arquivos JSON de animação
 * 
 * @param {Object} animationData - Dados JSON da animação Lottie
 * @param {boolean} loop - Se a animação deve repetir (default: true)
 * @param {boolean} autoplay - Se a animação começa automaticamente (default: true)
 * @param {React.ReactNode} fallback - Componente para mostrar se Lottie não carregar
 * @param {Object} style - Estilos CSS inline
 */
export function LottieAnimation({
  animationData,
  loop = true,
  autoplay = true,
  fallback = null,
  style = {},
}) {
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  useEffect(() => {
    // Importa lottie-web dinamicamente (evita aumentar o bundle)
    const loadLottie = async () => {
      try {
        const lottie = await import('lottie-web');
        
        if (!containerRef.current || !animationData) {
          setIsLoading(false);
          return;
        }

        // Renderiza a animação
        animationRef.current = lottie.default.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop,
          autoplay,
          animationData,
        });

        setIsLoading(false);
      } catch (err) {
        console.error('Erro ao carregar Lottie:', err);
        setError(true);
        setIsLoading(false);
      }
    };

    loadLottie();

    return () => {
      // Limpa a animação ao desmontar
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [animationData, loop, autoplay]);

  // Se houve erro ou Lottie não carregou, mostra o fallback
  if (error || (isLoading && fallback)) {
    return fallback || null;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        ...style,
      }}
    />
  );
}

export default LottieAnimation;
